import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import {
  MAT_FORM_FIELD,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';

// @see https://material.angular.dev/guide/creating-a-custom-form-field-control
@Component({
  selector: 'tb-file-upload',
  template: `<input
      [attr.id]="id"
      [attr.aria-labelledby]="_formField?.getLabelId()"
      type="file"
      [accept]="accept().join(',')"
      (change)="_handleInput($event.target['files'][0])"
      hidden
      #fileUpload
    />
    @if (shouldLabelFloat) {
      {{ _value()?.name }}
    } @else {
      {{ placeholder }}
    }
    @if (required) {
      *
    }`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    tb-file-upload {
      text-wrap: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      user-select: none;
      display: block;
    }
  `,
  providers: [
    { provide: MatFormFieldControl, useExisting: FileUploadComponent },
  ],
  host: {
    '[class.disabled]': '_disabled()',
    '[class.font-body-large]': 'true',
    '[class.color-on-surface-variant]': 'true',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class FileUploadComponent
  implements MatFormFieldControl<File>, ControlValueAccessor, OnDestroy
{
  // Inputs

  _userAriaDescribedBy = input<string>('', { alias: 'aria-describedby' });
  _placeholder = input<string>('', { alias: 'placeholder' });
  _value = model<File | null>(null, { alias: 'value' });
  _disabledByInput = input<boolean, unknown>(false, {
    alias: 'disabled',
    transform: booleanAttribute,
  });
  _required = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute,
  });
  accept = input<string[]>([]);

  // Injections

  _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  ngControl = inject(NgControl, { optional: true, self: true });
  _formField = inject(MAT_FORM_FIELD, {
    optional: true,
  });
  _focusMonitor = inject(FocusMonitor);
  _fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileUpload');

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      // Read signals to trigger effect.
      this._placeholder();
      this._required();
      this._disabled();
      this._focused();
      // Propagate state changes.
      untracked(() => this.stateChanges.next());
    });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  static nextId = 0;

  controlType = 'tb-file-upload';
  id = `file-upload-${FileUploadComponent.nextId++}`;

  stateChanges = new Subject<void>();
  _touched = signal(false);
  _focused = signal(false);
  _disabledByCva = signal(false);
  _disabled = computed(() => this._disabledByInput() || this._disabledByCva());

  get focused(): boolean {
    return this._focused();
  }

  get empty() {
    return !this._value();
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get userAriaDescribedBy() {
    return this._userAriaDescribedBy();
  }

  get placeholder(): string {
    return this._placeholder();
  }

  get required(): boolean {
    return this._required();
  }

  get disabled(): boolean {
    return this._disabled();
  }

  get value(): File | null {
    return this._value();
  }

  get errorState(): boolean {
    return this.required && this.empty && this._touched();
  }

  onChange: (file: File) => void = () => {};
  onTouched = () => {};

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
  }

  onContainerClick() {
    this._fileInput().nativeElement.click();
  }

  writeValue(file: File | null): void {
    this._updateValue(file);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onFocusIn() {
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent) {
    if (
      !this._elementRef.nativeElement.contains(event.relatedTarget as Element)
    ) {
      this._touched.set(true);
      this._focused.set(false);
      this.onTouched();
    }
  }

  _handleInput(newFile: File): void {
    if (newFile) {
      this._value.set(newFile);
      this.onChange(newFile);
    }
  }

  private _updateValue(newFile: File | null) {
    const current = this._value();
    if (newFile === current) {
      return;
    }
    this._value.set(newFile);
  }
}
